import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle, MapPin } from "@phosphor-icons/react";
import Reveal from "@/components/motion/Reveal";
import PageHero from "@/components/PageHero";
import { T, useContent } from "@/content/ContentContext";

const AUTHORITIES = [
  { k: "licensing.authority.1" },
  { k: "licensing.authority.2" },
  { k: "licensing.authority.3" },
];

const STEPS = [
  { n: "01", k: "licensing.step.1" },
  { n: "02", k: "licensing.step.2" },
  { n: "03", k: "licensing.step.3" },
  { n: "04", k: "licensing.step.4" },
  { n: "05", k: "licensing.step.5" },
];

export default function LicensingPage() {
  const { t, list } = useContent();
  const docs = list("licensing.docs.items");
  return (
    <div data-testid="licensing-page">
      <PageHero
        overline={t("licensing.hero.overline")}
        lines={[<T key="1" k="licensing.hero.line1" />, <T key="2" k="licensing.hero.line2" em="text-brand-gold" />]}
        intro={t("licensing.hero.intro")}
      />

      {/* AUTHORITIES */}
      <section className="bg-brand-bone py-24 lg:py-32">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight font-medium text-brand-ink mt-4" data-testid="authorities-heading">
              <T k="licensing.auth.heading" em="text-brand-green" />
            </h2>
            <p className="mt-6 max-w-2xl text-base lg:text-lg font-light leading-relaxed text-brand-ink/70">
              {t("licensing.auth.body")}
            </p>
          </Reveal>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-px bg-brand-ink/10 border border-brand-ink/10">
            {AUTHORITIES.map((a, i) => (
              <Reveal key={a.k} delay={i * 0.1} className="bg-brand-bone p-10 group transition-colors duration-500 hover:bg-white">
                <p className="font-serif text-5xl text-brand-green font-medium" data-testid={`authority-abbr-${t(`${a.k}.abbr`).toLowerCase()}`}>{t(`${a.k}.abbr`)}</p>
                <p className="mt-2 flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-brand-gold">
                  <MapPin size={14} weight="fill" /> {t(`${a.k}.emirate`)}
                </p>
                <h3 className="mt-4 font-serif text-xl font-semibold text-brand-ink">{t(`${a.k}.name`)}</h3>
                <p className="mt-3 text-sm font-light leading-relaxed text-brand-ink/65">{t(`${a.k}.body`)}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="bg-brand-deep text-brand-bone py-24 lg:py-32 texture-grain">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-12 gap-16">
          <div className="lg:col-span-4">
            <Reveal>
              <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight font-medium mt-4" data-testid="process-heading">{t("licensing.process.heading")}</h2>
              <p className="mt-6 text-base font-light leading-relaxed text-brand-bone/65">
                {t("licensing.process.body")}
              </p>
            </Reveal>
          </div>
          <div className="lg:col-span-8 border-t border-brand-bone/10">
            {STEPS.map((s, i) => (
              <Reveal key={s.n} delay={i * 0.06}>
                <div className="grid grid-cols-12 gap-6 py-8 border-b border-brand-bone/10 px-2" data-testid={`process-step-${s.n}`}>
                  <span className="col-span-2 sm:col-span-1 font-sans text-sm font-bold text-brand-gold tracking-[0.2em] pt-1">{s.n}</span>
                  <div className="col-span-10 sm:col-span-11">
                    <h3 className="font-serif text-xl lg:text-2xl font-medium">{t(`${s.k}.title`)}</h3>
                    <p className="mt-2 text-sm font-light leading-relaxed text-brand-bone/60 max-w-2xl">{t(`${s.k}.body`)}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* DOCUMENTS */}
      <section className="bg-brand-bone py-24 lg:py-32">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-12 gap-16">
          <div className="lg:col-span-5">
            <Reveal>
              <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight font-medium text-brand-ink mt-4" data-testid="documents-heading">{t("licensing.docs.heading")}</h2>
              <p className="mt-6 text-base font-light leading-relaxed text-brand-ink/70">
                {t("licensing.docs.body")}
              </p>
              <Link
                to="/contact"
                data-testid="documents-cta"
                className="mt-8 inline-flex items-center gap-3 rounded-full bg-brand-green px-8 py-4 text-sm font-semibold text-brand-bone transition-all duration-300 hover:bg-brand-pine hover:-translate-y-0.5"
              >
                {t("licensing.docs.cta")} <ArrowRight size={16} weight="bold" />
              </Link>
            </Reveal>
          </div>
          <div className="lg:col-span-7">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-brand-ink/10 border border-brand-ink/10">
              {docs.map((d, i) => (
                <Reveal key={d} delay={i * 0.04} className="bg-brand-bone px-6 py-5 flex items-center gap-3">
                  <CheckCircle size={20} weight="duotone" className="text-brand-green shrink-0" />
                  <span className="text-sm text-brand-ink/80">{d}</span>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
